import React, { useState, useEffect, useCallback } from 'react';
import { fundAPI, soaFieldCatalogAPI } from '../../services/api';
import { Plus, Trash2, Copy, Save, ChevronDown, ChevronRight, Upload, FileText, X, Check, Settings, Eye, Palette, Tag, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';

const SOATemplateDesigner = ({ fundId, canEdit }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldCatalog, setFieldCatalog] = useState(null);
  const [namingFields, setNamingFields] = useState([]);
  const [dateFormats, setDateFormats] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => { loadData(); }, [fundId]);

  const loadData = async () => {
    try {
      const [tmplRes, catalogRes] = await Promise.all([
        fundAPI.getSOATemplates(fundId),
        soaFieldCatalogAPI.getAll(),
      ]);
      setTemplates(tmplRes.data);
      setFieldCatalog(catalogRes.data.fields || catalogRes.data);
      setNamingFields(catalogRes.data.naming_fields || []);
      setDateFormats(catalogRes.data.date_formats || []);
    } catch (err) { console.error('Failed to load SOA templates:', err); }
    finally { setLoading(false); }
  };

  // Collect all investor_info field keys from catalog
  const getAllInfoFieldKeys = useCallback(() => {
    if (!fieldCatalog?.investor_info?.subsections) return [];
    const keys = [];
    Object.values(fieldCatalog.investor_info.subsections).forEach(sub => {
      sub.fields?.forEach(f => keys.push(f.key));
    });
    return keys;
  }, [fieldCatalog]);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      const allInfoFields = getAllInfoFieldKeys();
      const allSummaryFields = fieldCatalog?.investment_summary?.fields?.map(f => f.key) || [];
      const allTxnFields = fieldCatalog?.transactions?.fields?.map(f => f.key) || [];
      const allCommitmentTransferFields = fieldCatalog?.commitment_transfers?.fields?.map(f => f.key) || [];
      const res = await fundAPI.createSOATemplate(fundId, {
        name: createName.trim(),
        investor_info_fields: allInfoFields,
        investor_info_placement: {},
        investment_summary_fields: allSummaryFields,
        transaction_fields: allTxnFields,
        commitment_transfer_fields: allCommitmentTransferFields,
        transaction_sort_order: 'asc',
        statement_title: 'Statement of Accounts',
        number_format: 'indian',
        header_color: '#1a3a5c',
        font_color: '#ffffff',
        section_header_color: '#1a3a5c',
        class_name_color: '#1a3a5c',
        file_naming_pattern: 'SOA_{{account_name}}_{{fund_name}}',
        date_format: 'dd mmmm yyyy',
      });
      setTemplates(prev => [res.data, ...prev]);
      setCreateName('');
      setShowCreateForm(false);
      setSelectedTemplate(res.data);
      setEditMode(true);
      initEditData(res.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create template'); }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this SOA template?')) return;
    try {
      await fundAPI.deleteSOATemplate(fundId, templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) { setSelectedTemplate(null); setEditMode(false); }
    } catch (err) { alert('Failed to delete'); }
  };

  const handleDuplicate = async (tmpl) => {
    // Ask for a NEW name up-front so the clone always lands with a
    // distinct label. Default suggestion is `<original> (Copy)` — users
    // can edit inline. Empty / cancel aborts the duplicate.
    const suggested = `${tmpl.name} (Copy)`;
    const name = window.prompt('Name for the duplicated template', suggested);
    if (name === null) return;                       // user cancelled
    const clean = (name || '').trim();
    if (!clean) { alert('Name is required'); return; }
    try {
      const res = await fundAPI.duplicateSOATemplate(fundId, tmpl.id, clean);
      // Insert the new template right after the source so it lands next
      // to its parent in the sidebar. Then jump to it in edit mode.
      setTemplates(prev => {
        const idx = prev.findIndex(t => t.id === tmpl.id);
        const clone = res.data;
        if (idx < 0) return [...prev, clone];
        const next = [...prev];
        next.splice(idx + 1, 0, clone);
        return next;
      });
      handleSelectTemplate(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to duplicate template');
    }
  };

  const initEditData = useCallback((tmpl) => {
    setEditData({
      name: tmpl.name,
      sections: tmpl.sections || { investor_info: true, investment_summary: true, commitment_transfers: true, transactions: true },
      investor_info_fields: tmpl.investor_info_fields || [],
      investor_info_placement: tmpl.investor_info_placement || {},
      investor_info_show_label: tmpl.investor_info_show_label || {},
      investment_summary_fields: tmpl.investment_summary_fields || [],
      transaction_fields: tmpl.transaction_fields || [],
      commitment_transfer_fields: tmpl.commitment_transfer_fields || [],
      transaction_sort_order: tmpl.transaction_sort_order || 'asc',
      investment_summary_decimals: tmpl.investment_summary_decimals ?? 2,
      transaction_decimals: tmpl.transaction_decimals ?? 2,
      units_decimals: tmpl.units_decimals ?? 3,
      statement_title: tmpl.statement_title || 'Statement of Accounts',
      number_format: tmpl.number_format || 'indian',
      header_color: tmpl.header_color || '#1a3a5c',
      font_color: tmpl.font_color || '#ffffff',
      section_header_color: tmpl.section_header_color || '#1a3a5c',
      class_name_color: tmpl.class_name_color || '#1a3a5c',
      file_naming_pattern: tmpl.file_naming_pattern || 'SOA_{{account_name}}_{{fund_name}}',
      date_format: tmpl.date_format || 'dd mmmm yyyy',
    });
    setExpandedSections({ investor_info: true, investment_summary: true, commitment_transfers: true, transactions: true });
  }, []);

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setEditMode(false);
    initEditData(tmpl);
  };

  const handleSave = async () => {
    if (!selectedTemplate || !editData) return;
    setSaving(true);
    try {
      const res = await fundAPI.updateSOATemplate(fundId, selectedTemplate.id, editData);
      setSelectedTemplate(res.data);
      setTemplates(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      setEditMode(false);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTemplate) return;
    try {
      await fundAPI.uploadSOALogo(fundId, selectedTemplate.id, file);
      const res = await fundAPI.getSOATemplate(fundId, selectedTemplate.id);
      setSelectedTemplate(res.data);
      setTemplates(prev => prev.map(t => t.id === res.data.id ? res.data : t));
    } catch (err) { alert(err.response?.data?.detail || 'Logo upload failed'); }
  };

  const handlePreview = async () => {
    if (!selectedTemplate) return;
    setPreviewing(true);
    try {
      if (editMode) await handleSave();
      const res = await fundAPI.previewSOATemplate(fundId, selectedTemplate.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      alert(err.response?.data?.detail || 'Preview failed. Ensure at least one investor account has an investment in this fund.');
    }
    finally { setPreviewing(false); }
  };

  const toggleInfoField = (fieldKey) => {
    if (!editData) return;
    const current = editData.investor_info_fields;
    if (current.includes(fieldKey)) {
      setEditData(prev => ({ ...prev, investor_info_fields: current.filter(k => k !== fieldKey) }));
    } else {
      setEditData(prev => ({ ...prev, investor_info_fields: [...current, fieldKey] }));
    }
  };

  const toggleField = (section, fieldKey) => {
    if (!editData) return;
    const fieldName = `${section}_fields`;
    const current = editData[fieldName] || [];
    const updated = current.includes(fieldKey) ? current.filter(k => k !== fieldKey) : [...current, fieldKey];
    setEditData(prev => ({ ...prev, [fieldName]: updated }));
  };

  const toggleSection = (sectionKey) => {
    if (!editData) return;
    setEditData(prev => ({ ...prev, sections: { ...prev.sections, [sectionKey]: !prev.sections[sectionKey] } }));
  };

  const toggleExpandSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleAllInSubsection = (subsectionFields) => {
    if (!editData) return;
    const current = editData.investor_info_fields;
    const keys = subsectionFields.map(f => f.key);
    const allSelected = keys.every(k => current.includes(k));
    let updated;
    if (allSelected) {
      updated = current.filter(k => !keys.includes(k));
    } else {
      // Add missing keys at end, preserving existing order
      const toAdd = keys.filter(k => !current.includes(k));
      updated = [...current, ...toAdd];
    }
    setEditData(prev => ({ ...prev, investor_info_fields: updated }));
  };

  const toggleAllFieldsFlat = (sectionKey, fields) => {
    if (!editData) return;
    const fieldName = `${sectionKey}_fields`;
    const current = editData[fieldName] || [];
    const allSelected = fields.every(f => current.includes(f.key));
    const updated = allSelected ? current.filter(k => !fields.some(f => f.key === k)) : [...new Set([...current, ...fields.map(f => f.key)])];
    setEditData(prev => ({ ...prev, [fieldName]: updated }));
  };

  const moveInfoField = (fieldKey, direction) => {
    if (!editData) return;
    const arr = [...editData.investor_info_fields];
    const idx = arr.indexOf(fieldKey);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setEditData(prev => ({ ...prev, investor_info_fields: arr }));
  };

  // Phase O — per-field Left/Right column placement
  const setFieldSide = (fieldKey, side) => {
    if (!editData) return;
    setEditData(prev => ({
      ...prev,
      investor_info_placement: { ...(prev.investor_info_placement || {}), [fieldKey]: side },
    }));
  };

  // Phase O3 — Per-field "show label" toggle
  const getFieldShowLabel = (fieldKey) => {
    if (!editData) return true;
    const map = editData.investor_info_show_label || {};
    const v = map[fieldKey];
    return v === undefined ? true : !!v;
  };

  const toggleFieldShowLabel = (fieldKey) => {
    if (!editData) return;
    setEditData(prev => ({
      ...prev,
      investor_info_show_label: {
        ...(prev.investor_info_show_label || {}),
        [fieldKey]: !getFieldShowLabel(fieldKey),
      },
    }));
  };

  const getFieldSide = (fieldKey) => {
    if (!editData) return 'left';
    return (editData.investor_info_placement || {})[fieldKey] || 'left';
  };

  // Build flat label map for display
  const fieldLabelMap = {};
  if (fieldCatalog?.investor_info?.subsections) {
    Object.values(fieldCatalog.investor_info.subsections).forEach(sub => {
      sub.fields?.forEach(f => { fieldLabelMap[f.key] = f.label; });
    });
  }

  if (loading) return <div className="p-4 text-center text-gray-500 text-sm">Loading SOA templates...</div>;

  return (
    <div className="space-y-4" data-testid="soa-template-designer">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">SOA Templates</h3>
        {canEdit && (
          <button onClick={() => setShowCreateForm(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700" data-testid="create-soa-template-btn">
            <Plus size={16} /> New SOA Template
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <input type="text" value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="Template name..." className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="soa-template-name-input" autoFocus />
            <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700" data-testid="soa-template-create-confirm">Create</button>
            <button onClick={() => { setShowCreateForm(false); setCreateName(''); }} className="px-3 py-2 text-gray-500 hover:text-gray-700"><X size={18} /></button>
          </div>
        </div>
      )}

      <div className="flex gap-4">
        {/* Template List */}
        <div className="w-56 flex-shrink-0 space-y-2">
          {templates.length === 0 && !showCreateForm && <p className="text-sm text-gray-400 p-3">No SOA templates yet.</p>}
          {templates.map(tmpl => (
            <div key={tmpl.id} onClick={() => handleSelectTemplate(tmpl)}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedTemplate?.id === tmpl.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
              data-testid={`soa-template-item-${tmpl.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{tmpl.name}</span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDuplicate(tmpl); }}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Duplicate template — copy every setting under a new name"
                      data-testid={`duplicate-soa-template-${tmpl.id}`}
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(tmpl.id); }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"
                      title="Delete template"
                      data-testid={`delete-soa-template-${tmpl.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: tmpl.header_color || '#1a3a5c' }} />
                {tmpl.logo_storage_path && <span className="text-xs text-green-600">Logo</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Template Editor */}
        {selectedTemplate && editData && (
          <div className="flex-1 bg-white border rounded-lg p-5 space-y-4 overflow-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                {editMode ? (
                  <input type="text" value={editData.name} onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))} className="text-lg font-semibold border-b border-blue-300 focus:outline-none focus:border-blue-500 px-1" data-testid="soa-template-name-edit" />
                ) : (
                  <h4 className="text-lg font-semibold text-gray-800">{selectedTemplate.name}</h4>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handlePreview} disabled={previewing} className="flex items-center gap-1.5 px-3 py-1.5 border text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50" data-testid="soa-template-preview-btn">
                  <Eye size={14} /> {previewing ? 'Loading...' : 'Preview'}
                </button>
                {editMode ? (
                  <>
                    <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid="soa-template-save-btn">
                      <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => { setEditMode(false); initEditData(selectedTemplate); }} className="px-3 py-1.5 text-gray-500 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                  </>
                ) : canEdit ? (
                  <button onClick={() => setEditMode(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700" data-testid="soa-template-edit-btn">
                    <Settings size={14} /> Configure
                  </button>
                ) : null}
              </div>
            </div>

            {/* Logo */}
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {selectedTemplate.logo_storage_path ? (
                  <div className="w-20 h-12 bg-white border rounded flex items-center justify-center text-xs text-green-600"><Check size={16} /> Logo</div>
                ) : (
                  <div className="w-20 h-12 bg-gray-100 border border-dashed rounded flex items-center justify-center text-xs text-gray-400">No Logo</div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">AMC Logo</p>
                <p className="text-xs text-gray-500">Appears on each page of the SOA</p>
              </div>
              {canEdit && (
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-white border text-sm rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload size={14} /> Upload
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="soa-logo-upload" />
                </label>
              )}
            </div>

            {/* Settings */}
            {editMode && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Statement Title</label>
                    <input type="text" value={editData.statement_title} onChange={(e) => setEditData(prev => ({ ...prev, statement_title: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-statement-title-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Number Format</label>
                    <select value={editData.number_format} onChange={(e) => setEditData(prev => ({ ...prev, number_format: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-number-format-select">
                      <option value="indian">Indian (10,00,000)</option>
                      <option value="western">Western (1,000,000)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Transaction Sort</label>
                    <select value={editData.transaction_sort_order} onChange={(e) => setEditData(prev => ({ ...prev, transaction_sort_order: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-txn-sort-select">
                      <option value="asc">Ascending (oldest first)</option>
                      <option value="desc">Descending (newest first)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Date Format</label>
                    <select value={editData.date_format} onChange={(e) => setEditData(prev => ({ ...prev, date_format: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-date-format-select">
                      {dateFormats.map(df => (
                        <option key={df.key} value={df.key}>{df.example}</option>
                      ))}
                      {dateFormats.length === 0 && <option value="dd mmmm yyyy">12 April 2026</option>}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Palette size={12} /> Table Header Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editData.header_color} onChange={(e) => setEditData(prev => ({ ...prev, header_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid="soa-header-color-input" />
                      <input type="text" value={editData.header_color} onChange={(e) => setEditData(prev => ({ ...prev, header_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Palette size={12} /> Table Header Font</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editData.font_color} onChange={(e) => setEditData(prev => ({ ...prev, font_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid="soa-font-color-input" />
                      <input type="text" value={editData.font_color} onChange={(e) => setEditData(prev => ({ ...prev, font_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Palette size={12} /> Section Header Font</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editData.section_header_color} onChange={(e) => setEditData(prev => ({ ...prev, section_header_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid="soa-section-header-color-input" />
                      <input type="text" value={editData.section_header_color} onChange={(e) => setEditData(prev => ({ ...prev, section_header_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Palette size={12} /> Class Name Font</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={editData.class_name_color} onChange={(e) => setEditData(prev => ({ ...prev, class_name_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" data-testid="soa-class-name-color-input" />
                      <input type="text" value={editData.class_name_color} onChange={(e) => setEditData(prev => ({ ...prev, class_name_color: e.target.value }))} className="flex-1 px-2 py-1.5 border rounded text-sm font-mono" />
                    </div>
                  </div>
                </div>
                {/* Color previews */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium" style={{ backgroundColor: editData.header_color, color: editData.font_color }}>Table Header</div>
                  <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold border-b" style={{ color: editData.section_header_color }}>Investment Summary (INR)</div>
                  <div className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-50 rounded" style={{ color: editData.class_name_color }}>Class A</div>
                </div>
                {/* File naming pattern */}
                <div>
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Tag size={12} /> File Naming Pattern</label>
                  <input type="text" value={editData.file_naming_pattern} onChange={(e) => setEditData(prev => ({ ...prev, file_naming_pattern: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm font-mono" data-testid="soa-file-naming-input" />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {namingFields.map(f => (
                      <button key={f.key} type="button" onClick={() => setEditData(prev => ({ ...prev, file_naming_pattern: (prev.file_naming_pattern || '') + `{{${f.key}}}` }))}
                        className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded hover:bg-blue-50 hover:text-blue-600 border">
                        {`{{${f.key}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section / Field Selection */}
            {fieldCatalog && (
              <div className="space-y-3">
                {/* Investor Info — with reordering */}
                <div className={`border rounded-lg ${editData.sections.investor_info ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg cursor-pointer" onClick={() => toggleExpandSection('investor_info')}>
                    <div className="flex items-center gap-2">
                      {expandedSections.investor_info ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span className="font-medium text-sm text-gray-700">{fieldCatalog.investor_info?.label}</span>
                      <span className="text-xs text-gray-400">({editData.investor_info_fields.length} selected)</span>
                    </div>
                    {editMode && (
                      <label className="flex items-center gap-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={editData.sections.investor_info} onChange={() => toggleSection('investor_info')} className="rounded border-gray-300" data-testid="soa-section-toggle-investor_info" />
                        Include
                      </label>
                    )}
                  </div>
                  {expandedSections.investor_info && editData.sections.investor_info && (
                    <div className="p-3">
                      {/* Field picker by subsection */}
                      <div className="space-y-2 mb-4">
                        {Object.entries(fieldCatalog.investor_info.subsections).map(([subKey, sub]) => {
                          const allSel = sub.fields.every(f => editData.investor_info_fields.includes(f.key));
                          return (
                            <div key={subKey} className="border-l-2 border-blue-200 pl-3">
                              <div className="flex items-center justify-between mb-1 cursor-pointer" onClick={() => toggleExpandSection(subKey)}>
                                <div className="flex items-center gap-1.5">
                                  {expandedSections[subKey] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{sub.label}</span>
                                </div>
                                {editMode && <button onClick={(e) => { e.stopPropagation(); toggleAllInSubsection(sub.fields); }} className="text-xs text-blue-500 hover:text-blue-700">{allSel ? 'Deselect All' : 'Select All'}</button>}
                              </div>
                              {expandedSections[subKey] && (
                                <div className="grid grid-cols-2 gap-1">
                                  {sub.fields.map(f => (
                                    <label key={f.key} className="flex items-center gap-1.5 text-xs text-gray-600 py-0.5">
                                      {editMode ? <input type="checkbox" checked={editData.investor_info_fields.includes(f.key)} onChange={() => toggleInfoField(f.key)} className="rounded border-gray-300 text-blue-600" data-testid={`soa-field-${f.key}`} /> : <span className={`w-3 h-3 rounded-sm border ${editData.investor_info_fields.includes(f.key) ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`} />}
                                      {f.label}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Placement order + Left/Right column */}
                      {editMode && editData.investor_info_fields.length > 0 && (
                        <div className="border-t pt-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1"><GripVertical size={12} /> Field Order &amp; Column Placement</p>
                          <p className="text-[11px] text-gray-500 mb-2">Choose which column each field appears in. Within a column, the order below is preserved.</p>
                          <div className="space-y-1 max-h-72 overflow-y-auto">
                            {editData.investor_info_fields.map((key, idx) => {
                              const side = getFieldSide(key);
                              return (
                                <div key={key} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded border text-xs">
                                  <span className="text-gray-400 w-4 text-center">{idx + 1}</span>
                                  <span className="flex-1 text-gray-700 truncate">{fieldLabelMap[key] || key}</span>
                                  <label
                                    className="flex items-center gap-1 mr-1 cursor-pointer select-none"
                                    title="Show field label/heading in SOA"
                                    data-testid={`soa-field-showlabel-wrap-${key}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={getFieldShowLabel(key)}
                                      onChange={() => toggleFieldShowLabel(key)}
                                      className="h-3.5 w-3.5 accent-blue-600"
                                      data-testid={`soa-field-showlabel-${key}`}
                                    />
                                    <span className="text-[11px] text-gray-600">Show label</span>
                                  </label>
                                  <div className="flex items-center gap-1 mr-1" data-testid={`soa-field-side-${key}`}>
                                    <button
                                      type="button"
                                      onClick={() => setFieldSide(key, 'left')}
                                      className={`px-2 py-0.5 rounded text-[11px] border ${side === 'left' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50'}`}
                                      data-testid={`soa-field-left-${key}`}
                                    >
                                      Left
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setFieldSide(key, 'right')}
                                      className={`px-2 py-0.5 rounded text-[11px] border ${side === 'right' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50'}`}
                                      data-testid={`soa-field-right-${key}`}
                                    >
                                      Right
                                    </button>
                                  </div>
                                  <button onClick={() => moveInfoField(key, 'up')} disabled={idx === 0} className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30" data-testid={`soa-field-up-${key}`}><ArrowUp size={12} /></button>
                                  <button onClick={() => moveInfoField(key, 'down')} disabled={idx === editData.investor_info_fields.length - 1} className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30" data-testid={`soa-field-down-${key}`}><ArrowDown size={12} /></button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Investment Summary */}
                <SectionBlock title={fieldCatalog.investment_summary?.label || 'Investment Summary'} sectionKey="investment_summary" enabled={editData.sections.investment_summary} onToggleSection={() => toggleSection('investment_summary')} expanded={expandedSections.investment_summary} onToggleExpand={() => toggleExpandSection('investment_summary')} editMode={editMode}>
                  <FieldList fields={fieldCatalog.investment_summary?.fields || []} selectedFields={editData.investment_summary_fields} onToggleField={(k) => toggleField('investment_summary', k)} onToggleAll={() => toggleAllFieldsFlat('investment_summary', fieldCatalog.investment_summary?.fields || [])} editMode={editMode} />
                  {/* Phase O3 — Decimal precision pickers */}
                  <div className="border-t pt-3 flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Amount Decimals</span>
                      {editMode ? (
                        <select
                          value={editData.investment_summary_decimals ?? 2}
                          onChange={(e) => setEditData(prev => ({ ...prev, investment_summary_decimals: parseInt(e.target.value, 10) }))}
                          className="border rounded-lg px-2 py-1 text-sm bg-white"
                          data-testid="soa-investment-summary-decimals"
                        >
                          <option value={0}>0 (whole number)</option>
                          <option value={1}>1</option>
                          <option value={2}>2 (default)</option>
                          <option value={3}>3</option>
                          <option value={4}>4</option>
                        </select>
                      ) : (
                        <span className="text-xs text-gray-700 px-2 py-1 bg-gray-50 rounded">{editData.investment_summary_decimals ?? 2}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Units Decimals</span>
                      {editMode ? (
                        <select
                          value={editData.units_decimals ?? 3}
                          onChange={(e) => setEditData(prev => ({ ...prev, units_decimals: parseInt(e.target.value, 10) }))}
                          className="border rounded-lg px-2 py-1 text-sm bg-white"
                          data-testid="soa-units-decimals"
                        >
                          <option value={0}>0</option>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3 (default)</option>
                          <option value={4}>4</option>
                          <option value={5}>5</option>
                        </select>
                      ) : (
                        <span className="text-xs text-gray-700 px-2 py-1 bg-gray-50 rounded">{editData.units_decimals ?? 3}</span>
                      )}
                    </div>
                  </div>
                </SectionBlock>

                {/* Commitment Transfer Summary — between Investment Summary and Transactions */}
                <SectionBlock title={fieldCatalog.commitment_transfers?.label || 'Commitment Summary'} sectionKey="commitment_transfers" enabled={editData.sections.commitment_transfers} onToggleSection={() => toggleSection('commitment_transfers')} expanded={expandedSections.commitment_transfers} onToggleExpand={() => toggleExpandSection('commitment_transfers')} editMode={editMode}>
                  <FieldList fields={fieldCatalog.commitment_transfers?.fields || []} selectedFields={editData.commitment_transfer_fields || []} onToggleField={(k) => toggleField('commitment_transfer', k)} onToggleAll={() => toggleAllFieldsFlat('commitment_transfer', fieldCatalog.commitment_transfers?.fields || [])} editMode={editMode} />
                  <div className="text-[11px] text-gray-500 border-t pt-2">
                    Rows are drawn from transactions with type <span className="font-semibold">Commitment Agreed</span> and <span className="font-semibold">Commitment Transferred</span>. Sort order follows the Transaction Details section.
                  </div>
                </SectionBlock>

                {/* Transactions */}
                <SectionBlock title={fieldCatalog.transactions?.label || 'Transaction Details'} sectionKey="transactions" enabled={editData.sections.transactions} onToggleSection={() => toggleSection('transactions')} expanded={expandedSections.transactions} onToggleExpand={() => toggleExpandSection('transactions')} editMode={editMode}>
                  <FieldList fields={fieldCatalog.transactions?.fields || []} selectedFields={editData.transaction_fields} onToggleField={(k) => toggleField('transaction', k)} onToggleAll={() => toggleAllFieldsFlat('transaction', fieldCatalog.transactions?.fields || [])} editMode={editMode} />
                  {/* Phase O3 — Decimal picker */}
                  <div className="border-t pt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Amount Decimals</span>
                    {editMode ? (
                      <select
                        value={editData.transaction_decimals ?? 2}
                        onChange={(e) => setEditData(prev => ({ ...prev, transaction_decimals: parseInt(e.target.value, 10) }))}
                        className="border rounded-lg px-2 py-1 text-sm bg-white"
                        data-testid="soa-transaction-decimals"
                      >
                        <option value={0}>0 (whole number)</option>
                        <option value={1}>1</option>
                        <option value={2}>2 (default)</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                      </select>
                    ) : (
                      <span className="text-xs text-gray-700 px-2 py-1 bg-gray-50 rounded">{editData.transaction_decimals ?? 2}</span>
                    )}
                  </div>
                  {/* Phase O — Sort order picker inside the Transaction Details section */}
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Sort Transactions By Date</p>
                    {editMode ? (
                      <select
                        value={editData.transaction_sort_order || 'asc'}
                        onChange={(e) => setEditData(prev => ({ ...prev, transaction_sort_order: e.target.value }))}
                        className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                        data-testid="soa-transaction-sort-order"
                      >
                        <option value="asc">Ascending (oldest first)</option>
                        <option value="desc">Descending (newest first)</option>
                      </select>
                    ) : (
                      <span className="text-xs text-gray-700 px-2 py-1 bg-gray-50 rounded">
                        {editData.transaction_sort_order === 'desc' ? 'Descending (newest first)' : 'Ascending (oldest first)'}
                      </span>
                    )}
                  </div>
                </SectionBlock>
              </div>
            )}
          </div>
        )}
        {!selectedTemplate && templates.length > 0 && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm border rounded-lg bg-gray-50 min-h-[200px]">Select a template to configure</div>
        )}
      </div>
    </div>
  );
};

const SectionBlock = ({ title, sectionKey, enabled, onToggleSection, expanded, onToggleExpand, editMode, children }) => (
  <div className={`border rounded-lg ${enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg cursor-pointer" onClick={onToggleExpand}>
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-medium text-sm text-gray-700">{title}</span>
      </div>
      {editMode && (
        <label className="flex items-center gap-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={enabled} onChange={onToggleSection} className="rounded border-gray-300" data-testid={`soa-section-toggle-${sectionKey}`} />
          Include
        </label>
      )}
    </div>
    {expanded && enabled && <div className="p-3 space-y-3">{children}</div>}
  </div>
);

const FieldList = ({ fields, selectedFields, onToggleField, onToggleAll, editMode }) => {
  const allSelected = fields.every(f => selectedFields.includes(f.key));
  return (
    <div>
      {editMode && <div className="flex justify-end mb-1.5"><button onClick={onToggleAll} className="text-xs text-blue-500 hover:text-blue-700">{allSelected ? 'Deselect All' : 'Select All'}</button></div>}
      <div className="grid grid-cols-2 gap-1.5">
        {fields.map(f => (
          <label key={f.key} className="flex items-center gap-1.5 text-xs text-gray-600 py-0.5">
            {editMode ? <input type="checkbox" checked={selectedFields.includes(f.key)} onChange={() => onToggleField(f.key)} className="rounded border-gray-300 text-blue-600" data-testid={`soa-field-${f.key}`} /> : <span className={`w-3 h-3 rounded-sm border ${selectedFields.includes(f.key) ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`} />}
            {f.label}
          </label>
        ))}
      </div>
    </div>
  );
};

export default SOATemplateDesigner;
